export const PDF_PRINT_HEIGHT_RESET_STYLE = `<style data-pdf-print-reset>
@media print {
  html, body { height: auto !important; min-height: 0 !important; }
  body { display: block !important; margin: 0 !important; }
  #main-content { display: block !important; flex: 0 0 auto !important; }
}
</style>`;

const EMPTY_NODE_SOURCE = '<(p|div|span|h[1-6])\\b[^>]*>\\s*(?:&nbsp;|<br\\s*\\/?>|\\s)*<\\/\\1>';
const PAGE_BREAK_MARKER_SOURCE =
  '<(?:div|p)\\b[^>]*style=["\'][^"\']*(?:page-break-before\\s*:\\s*always|break-before\\s*:\\s*page)[^"\']*["\'][^>]*>\\s*(?:&nbsp;|<br\\s*\\/?>|\\s)*<\\/(?:div|p)>';

function isPageBreakAttrs(attrs: string): boolean {
  const attrText = String(attrs || '').toLowerCase();
  return /page-break-before\s*:\s*always/.test(attrText) || /break-before\s*:\s*page/.test(attrText);
}

/** True when leftover markup cannot produce a printed page of its own. */
export function isInertPdfTail(html: string): boolean {
  let rest = String(html || '');
  rest = rest.replace(/<!--[\s\S]*?-->/g, '');
  rest = rest.replace(/<footer\b[^>]*>[\s\S]*?<\/footer>/gi, '');
  rest = rest.replace(new RegExp(EMPTY_NODE_SOURCE, 'gi'), '');
  rest = rest.replace(/<br\s*\/?>/gi, '');
  rest = rest.replace(/&nbsp;/gi, '');
  rest = rest.replace(/<\/(?:p|div|span|section|article|main|body|html)>/gi, '');
  rest = rest.replace(/<\/?(?:html|body|head|meta|title|link|style|base)\b[^>]*>/gi, '');
  return !rest.replace(/\s+/g, '');
}

function stripEmptyBlocksKeepPageBreaks(html: string): string {
  return html.replace(new RegExp(EMPTY_NODE_SOURCE, 'gi'), (full) => {
    const open = String(full).match(/^<[^>]+>/i)?.[0] || '';
    return isPageBreakAttrs(open) ? full : '';
  });
}

/**
 * Drop forced page-breaks that only have empty nodes, wrappers, or the repeating footer after them.
 * The previous end-of-string strip missed the issued-quote case: page-break + empty <p> + </div> + footer.
 */
export function stripTrailingEmptyPdfPagesHtml(inputHtml: string): string {
  let html = String(inputHtml || '');
  html = stripEmptyBlocksKeepPageBreaks(html);

  while (true) {
    const matches = [...html.matchAll(new RegExp(PAGE_BREAK_MARKER_SOURCE, 'gi'))];
    if (!matches.length) break;
    const last = matches[matches.length - 1];
    const start = last.index ?? -1;
    if (start < 0) break;
    const after = html.slice(start + last[0].length);
    if (!isInertPdfTail(after)) break;
    html = `${html.slice(0, start)}${after}`;
    html = stripEmptyBlocksKeepPageBreaks(html);
  }

  return html;
}

export function prepareHtmlForPdf(html: string): string {
  return withPdfPrintHeightReset(stripTrailingEmptyPdfPagesHtml(html));
}

/** Chromium print treats html/body height:100% as a full extra page once header/footer margins are reserved. */
export function withPdfPrintHeightReset(html: string): string {
  const raw = String(html || '');
  if (/data-pdf-print-reset/.test(raw)) return raw;
  if (/<\/head>/i.test(raw)) return raw.replace(/<\/head>/i, `${PDF_PRINT_HEIGHT_RESET_STYLE}</head>`);
  return `${PDF_PRINT_HEIGHT_RESET_STYLE}${raw}`;
}
